import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { clsx } from 'clsx';

const MathField = forwardRef(({
  value = '',
  onChange,
  onFocus,
  onBlur,
  placeholder = 'Enter math expression...',
  className,
  readOnly = false,
  autoFocus = false,
}, ref) => {
  const mathFieldRef = useRef(null);
  const containerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getValue: () => mathFieldRef.current?.getValue() || '',
    setValue: (newValue) => {
      if (mathFieldRef.current) {
        mathFieldRef.current.setValue(newValue);
      }
    },
    focus: () => mathFieldRef.current?.focus(),
    blur: () => mathFieldRef.current?.blur(),
  }));

  useEffect(() => {
    const initializeMathField = async () => {
      if (typeof window === 'undefined') return;
      
      try {
        // Dynamic import for MathLive
        const { MathfieldElement } = await import('mathlive');
        
        if (!containerRef.current || mathFieldRef.current) return;

        // Create math field element
        const mathfield = new MathfieldElement({
          value: value,
          readOnly: readOnly,
          virtualKeyboardMode: 'auto',
          smartMode: true,
          smartFence: true,
          smartSuperscript: true,
          removeExtraneousParentheses: true,
        });

        // Set up event listeners
        mathfield.addEventListener('input', (event) => {
          onChange?.(event.target.getValue());
        });

        mathfield.addEventListener('focus', () => {
          onFocus?.();
        });

        mathfield.addEventListener('blur', () => {
          onBlur?.();
        });

        // Apply custom styling
        mathfield.style.width = '100%';
        mathfield.style.minHeight = '40px';
        mathfield.style.border = 'none';
        mathfield.style.outline = 'none';
        mathfield.style.fontSize = '16px';

        containerRef.current.appendChild(mathfield);
        mathFieldRef.current = mathfield;

        if (autoFocus) {
          setTimeout(() => mathfield.focus(), 100);
        }

      } catch (error) {
        console.error('Failed to initialize MathField:', error);
      }
    };

    initializeMathField();

    return () => {
      if (mathFieldRef.current && containerRef.current) {
        try {
          containerRef.current.removeChild(mathFieldRef.current);
        } catch (e) {
          // Element already removed
        }
        mathFieldRef.current = null;
      }
    };
  }, []);

  // Update value when prop changes
  useEffect(() => {
    if (mathFieldRef.current && mathFieldRef.current.getValue() !== value) {
      mathFieldRef.current.setValue(value);
    }
  }, [value]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        'relative w-full rounded-lg border border-gray-300 bg-white px-3 py-2',
        'focus-within:border-sigma-500 focus-within:ring-1 focus-within:ring-sigma-500',
        'disabled:bg-gray-50 disabled:text-gray-500',
        readOnly && 'bg-gray-50 cursor-not-allowed',
        className
      )}
    />
  );
});

MathField.displayName = 'MathField';

export default MathField;